import { Injectable } from '@angular/core';
import { Loading, LoadingController } from 'ionic-angular';
import { ConfigProvider } from '../config/config';
import { LoggerProvider, LoggerEvent } from '../logger/logger';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';

@Injectable()
export class LoadingProvider {
  public hideCalledRecently = false;
  loading: Loading = null;
  showingLoading: boolean = false;
  timeout;
  emergencyTimeout;
  delayed = null;
  delayedHide = null;
  animnode = null;
  anim = null;
  animId = 0;
  dismissPromise = null;

  constructor(private loadingCtrl: LoadingController) {
  }

  public setAnim(anim) {
    this.anim = anim;
  }

  public keepAlive() {
    this.setTimeout();
  }

  private setTimeout() {
    // Guard on showingLoading (not this.timeout): the timeout is only ever assigned inside this
    // block, so the previous `if (this.timeout)` check could never become true on a fresh show()
    // and the safety/emergency timeout never armed - leaving a show() without a reachable hide()
    // (e.g. an operation stuck in a resume/refresh loop) on screen forever.
    if (this.showingLoading) {
      // timeout can be kept alive by HTTP provider i.e. several slow calls but if for whatever reason all the time calls are started in a loop we have a emergency timeout which doesn't get restarted
      if (!this.emergencyTimeout) {
        this.emergencyTimeout = window.setTimeout(() => {
          this.showTimeout();        
        }, (ConfigProvider.settings.loadingTimeoutMs * 2));
      }      
      window.clearTimeout(this.timeout);
      this.timeout = window.setTimeout(() => {
        this.showTimeout();      
      }, ConfigProvider.settings.loadingTimeoutMs);
    }
  }

  private showTimeout() {
    LoggerProvider.critical(this, LoggerEvent.LoadingTimeout, 'Loading has been cancelled !');
    RmtPopupComponent.showErrorMessage('Action timed out - please try it again later', null, false);
    this.hide();
  }

  public showDelayed(message: string = 'LOADING') {
    this.keepAlive();
    if (this.delayed)
      window.clearTimeout(this.delayed);
    if (this.delayedHide)
      window.clearTimeout(this.delayedHide);
    this.delayedHide = null;

    this.delayed = window.setTimeout(() => {
      this.show(message);
    }, 250);
  }

  public hideDelayed() {
    this.keepAlive();
    if (this.delayed)
      window.clearTimeout(this.delayed);
    this.delayed = null;
    if (this.delayedHide)
      window.clearTimeout(this.delayedHide);

    this.delayedHide = window.setTimeout(() => {
      this.hide();
    }, 250);
  }

  public isLoading() {
    return (this.loading) ? true : false;
  }

  public show(message: string = 'LOADING') {
    if (this.delayed)
      window.clearTimeout(this.delayed);
    this.delayed = null;
    if (this.delayedHide)
      window.clearTimeout(this.delayedHide);
    this.delayedHide = null;    

    if (this.loading === null) {
      if (this.animId > 1000)
        this.animId = 0;
      this.animId++;
      const id = this.animId;
      const loading = this.loadingCtrl.create({
        content: message,
        spinner: 'crescent',
        cssClass: 'ria-loader ria-loader-' + id
      });
      this.loading = loading;
      this.showingLoading = true;
      this.loading.present();
      this.styleLoading(id);
    }
    // Arm the auto-cancel timeout AFTER showingLoading is set, so it actually starts on a fresh
    // show(). This guarantees the overlay is dismissed (with a timeout message) instead of staying
    // up forever if the awaited operation never completes.
    this.setTimeout();
  }

  private async styleLoading(id, repeat = true) {
    if (!repeat && this.dismissPromise)
      await this.dismissPromise;
    window.requestAnimationFrame(() => {
      if (this.animId !== id)
        return;

      if (!this.animnode) {
        const anim = document.getElementsByClassName('anim-loading');
        if (anim && anim.length > 0)
          this.animnode = anim[0];
      }
      const loader = document.getElementsByClassName('ria-loader-' + id);
      if (this.animnode && loader && loader.length > 0) {          
        this.animnode.parentNode.removeChild(this.animnode);
        const spinner = loader[0].getElementsByClassName('loading-spinner');
        spinner[0].appendChild(this.animnode);
        if (this.anim)
          this.anim.play();
      }
      else if (repeat)        
        this.styleLoading(id, false);      
    });

  }

  public hide() {
    this.hideCalledRecently = true;
    if (this.timeout)
      window.clearTimeout(this.timeout);
    this.timeout = null;
    if (this.emergencyTimeout)
      window.clearTimeout(this.emergencyTimeout);
    this.emergencyTimeout = null;
    if (this.delayed)
      window.clearTimeout(this.delayed);
    this.delayed = null;
    if (this.delayedHide)
      window.clearTimeout(this.delayedHide);
    this.delayedHide = null;    

    let promise = null;
    if (this.loading) {
      const id = this.animId;
      promise = this.loading.dismiss().then(() => {
        if (this.animId !== id)
          return;

        this.dismissPromise = null;
        this.animId++;
        if (this.anim && !this.loading)
          this.anim.stop();
      });
      this.dismissPromise = promise;
      this.loading = null;
      this.showingLoading = false;
    }
    return promise;
  }

  showWaitingScreen(img: string, message?: string) {
    if(!this.showingLoading){
      this.loading = this.loadingCtrl.create({
        spinner: 'hide',
        content: this.buildWaitingScreen(img, message),
        cssClass: 'loading-waiting-background'
      });
      this.loading.present();
      this.showingLoading = true;
    }
  }

  private buildWaitingScreen(img: string, message?: string) {
    let content = `<img item-center src="${img}" class="loadingIcon">` +
                  `<div class="loadingMsgTxt">${message}</div>`
    return content;
  }
}



// WEBPACK FOOTER //
// ./src/providers/loading/loading.ts